import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { ArrowBack } from "@mui/icons-material"
import { Alert, Box, Button, CircularProgress, Typography } from "@mui/material"
import type { SxProps, Theme } from "@mui/material"
import { NotFoundError } from "api/errors"
import { useAppDispatch } from "hooks/redux"
import { useAccountAlias } from "resources/account"
import { useMarkScheduledWorkflowDeliverableRead, useScheduledWorkflowDeliverable } from "resources/scheduled-workflows/hooks"
import { buildScheduledWorkflowFileUrl } from "resources/scheduled-workflows/routes"
import type { ScheduledWorkflowFileUrlKind } from "resources/scheduled-workflows/routes"
import type { ScheduledWorkflowDeliverableFile, ScheduledWorkflowListItem, ScheduledWorkflowRun } from "resources/scheduled-workflows/types"
import { closeSkillsPane, setSurfaceView } from "../store/agent-chat-slice"
import type { ArtifactEnvelope, FileArtifact } from "../chatbox/artifact-contract"
import { ArtifactPanelBody } from "../chatbox/artifact-renderers"
import { useArtifactDownload } from "../chatbox/use-artifact-download"
import { runOutcomeWord } from "./latest-report"
import { primaryFileOf } from "./report-file"
import { reportEntryLabel, reportSourceLine } from "./report-format"
import { RunHistory } from "./reports-section"
import { RowActions } from "./row-actions"
import { viewerTimeZone } from "./schedule-format"

const CONTEXT_SX = { display: "flex", alignItems: "center", gap: 1, pb: 1.5 } satisfies SxProps<Theme>
const SOURCE_SX = { flex: 1, minWidth: 0, color: "text.secondary" } satisfies SxProps<Theme>
const WORKFLOW_NAME_SX = { color: "text.primary" } satisfies SxProps<Theme>
const FILE_DOWNLOAD_SX = { p: 0, minWidth: 0, textTransform: "none", fontWeight: "normal" } satisfies SxProps<Theme>
const VIEW_SX = { display: "flex", flexDirection: "column", flex: 1, minHeight: 0, gap: 1 } satisfies SxProps<Theme>
const VIEW_HEADER_SX = { display: "flex", alignItems: "center", gap: 1 } satisfies SxProps<Theme>
const VIEW_TITLE_SX = { flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" } satisfies SxProps<Theme>
const VIEW_BODY_SX = { display: "flex", flex: 1, minHeight: 0, gap: 2 } satisfies SxProps<Theme>
const VIEW_REPORT_SX = { flex: 1, minWidth: 0, overflow: "auto" } satisfies SxProps<Theme>
const VIEW_RUNS_SX = { width: 240, flexShrink: 0, overflow: "auto" } satisfies SxProps<Theme>
const PARTIAL_LABEL_SX = { color: "error.main", pb: 1 } satisfies SxProps<Theme>
const IGNORE = () => undefined
const IGNORE_MARK_READ_ERROR = () => null
const OTHER_FILES_SX = { display: "flex", flexDirection: "column", gap: 0.5, pt: 1.5, m: 0, pl: 0, listStyle: "none" } satisfies SxProps<Theme>

function reportFileArtifact(deliverableId: string, file: ScheduledWorkflowDeliverableFile): FileArtifact {
  return { id: deliverableId, type: "file", filename: file.filename, contentType: file.contentType, deliverable: { deliverableId, fileId: file.fileId } }
}

export const REPORT_PANEL_NAME = "Report"

export function reportArtifactOf(deliverableId: string): ArtifactEnvelope {
  // no filename: there is no file to name, so isFileArtifact keeps this out of the file renderer
  return { id: deliverableId, type: "file", title: REPORT_PANEL_NAME, deliverable: { deliverableId } }
}

function useReportFileSource(deliverableId: string, file: ScheduledWorkflowDeliverableFile) {
  const accountAlias = useAccountAlias()
  const artifact = useMemo(() => reportFileArtifact(deliverableId, file), [deliverableId, file])
  const urlFor = useCallback(
    (kind: ScheduledWorkflowFileUrlKind) => buildScheduledWorkflowFileUrl({ accountAlias, deliverableId, fileId: file.fileId }, kind),
    [accountAlias, deliverableId, file.fileId],
  )
  return { artifact, urlFor }
}

interface ReportFileProps {
  deliverableId: string
  file: ScheduledWorkflowDeliverableFile
  isExpanded: boolean
}

function ReportFile({ deliverableId, file, isExpanded }: ReportFileProps) {
  const { artifact, urlFor } = useReportFileSource(deliverableId, file)
  return <ArtifactPanelBody artifact={artifact} isExpanded={isExpanded} threadId={null} urlFor={urlFor} />
}

interface ReportFileDownloadProps {
  deliverableId: string
  file: ScheduledWorkflowDeliverableFile
}

function ReportFileDownload({ deliverableId, file }: ReportFileDownloadProps) {
  const { artifact, urlFor } = useReportFileSource(deliverableId, file)
  const { downloadArtifact, isDownloading } = useArtifactDownload(artifact, null, null, urlFor)
  const handleDownload = useCallback(() => downloadArtifact(null, deliverableId), [downloadArtifact, deliverableId])
  return (
    <Button variant="text" size="small" aria-label={`Download ${file.filename}`} disabled={isDownloading} onClick={handleDownload} sx={FILE_DOWNLOAD_SX}>
      {file.filename}
    </Button>
  )
}

interface OtherFilesProps {
  deliverableId: string
  files: ScheduledWorkflowDeliverableFile[]
}

function OtherFiles({ deliverableId, files }: OtherFilesProps) {
  if (files.length === 0) return null
  return (
    <Box component="ul" sx={OTHER_FILES_SX}>
      {files.map((file) => (
        <li key={file.fileId}><ReportFileDownload deliverableId={deliverableId} file={file} /></li>
      ))}
    </Box>
  )
}

interface ReportPanelBodyProps {
  deliverableId: string
  fileId?: string
  isExpanded: boolean
  isInline?: boolean
  onClose: () => void
  onName: (name: { deliverableId: string, text: string }) => void
}

export function ReportPanelBody({ deliverableId, fileId, isExpanded, isInline, onClose, onName }: ReportPanelBodyProps) {
  const dispatch = useAppDispatch()
  const { data: report, isLoading, error, refetch } = useScheduledWorkflowDeliverable(deliverableId)
  const file = report == null ? undefined : report.files.find((candidate) => candidate.fileId === fileId) ?? primaryFileOf(report.files)
  const openWorkflow = useCallback(() => {
    dispatch(closeSkillsPane())
    dispatch(setSurfaceView("workflows"))
    onClose()
  }, [dispatch, onClose])
  const { mutate: markRead } = useMarkScheduledWorkflowDeliverableRead(IGNORE_MARK_READ_ERROR)
  const markedDeliverableIds = useRef(new Set<string>())
  const retriedDeliverableIds = useRef(new Set<string>())
  const [markReadAttempt, setMarkReadAttempt] = useState(0)

  useEffect(() => {
    if (report != null) onName({ deliverableId, text: reportEntryLabel(report, viewerTimeZone()) })
  }, [report, deliverableId, onName])

  // every entry point (sidebar, latest-report cell, inline view) renders this body, so it owns mark-read
  useEffect(() => {
    if (report == null || report.read || markedDeliverableIds.current.has(deliverableId)) return
    markedDeliverableIds.current.add(deliverableId)
    markRead(deliverableId, {
      onError: () => {
        if (retriedDeliverableIds.current.has(deliverableId)) return
        retriedDeliverableIds.current.add(deliverableId)
        markedDeliverableIds.current.delete(deliverableId)
        setMarkReadAttempt((attempt) => attempt + 1)
      },
    })
  }, [report, deliverableId, markRead, markReadAttempt])

  if (isLoading) return <CircularProgress size={24} aria-label="Loading report" />
  // no cached report to fall back on: a failed background refetch keeps showing the cached one
  if (report == null && error != null && !(error instanceof NotFoundError)) {
    return (
      <Alert severity="error" action={<Button size="small" onClick={() => refetch()}>Try again</Button>}>Couldn&apos;t load this report. Please try again.</Alert>
    )
  }
  if (report == null || file == null) return <Alert severity="error">This report is no longer available.</Alert>

  const otherFiles = report.files.filter((candidate) => candidate.fileId !== file.fileId)
  const source = reportSourceLine(report.workflowName, report.workflowSchedule, viewerTimeZone(), new Date())
  return (
    <>
      {!isInline && (
        <Box sx={CONTEXT_SX}>
          <Typography variant="body2" sx={SOURCE_SX}>
            {source.before}<Box component="strong" sx={WORKFLOW_NAME_SX}>{source.workflowName}</Box>{source.after}
          </Typography>
          <Button size="small" variant="outlined" onClick={openWorkflow}>Open workflow</Button>
        </Box>
      )}
      <ReportFile deliverableId={deliverableId} file={file} isExpanded={isExpanded} />
      <OtherFiles deliverableId={deliverableId} files={otherFiles} />
    </>
  )
}

interface WorkflowReportViewProps {
  workflow: ScheduledWorkflowListItem
  onBack: () => void
}

export function WorkflowReportView({ workflow, onBack }: WorkflowReportViewProps) {
  const [selectedRun, setSelectedRun] = useState<ScheduledWorkflowRun | null>(null)
  const deliverableId = selectedRun == null ? workflow.latestDeliverable?.deliverableId ?? null : selectedRun.deliverable?.deliverableId ?? null
  const partialWord = selectedRun == null ? null : runOutcomeWord(selectedRun.outcome)

  return (
    <Box sx={VIEW_SX} data-testid="workflow-report-view">
      <Box sx={VIEW_HEADER_SX}>
        <Button size="small" variant="text" startIcon={<ArrowBack fontSize="small" />} onClick={onBack}>Back</Button>
        <Typography variant="h6" component="h2" sx={VIEW_TITLE_SX}>{workflow.name}</Typography>
        {workflow.archivedAt == null && <RowActions workflow={workflow} />}
      </Box>
      <Box sx={VIEW_BODY_SX}>
        <Box sx={VIEW_REPORT_SX}>
          {partialWord != null && <Typography variant="body2" sx={PARTIAL_LABEL_SX}>{partialWord}</Typography>}
          {deliverableId == null
            ? <Typography variant="body2" color="textSecondary">No report yet.</Typography>
            : <ReportPanelBody key={deliverableId} deliverableId={deliverableId} isExpanded isInline onClose={IGNORE} onName={IGNORE} />}
        </Box>
        <Box sx={VIEW_RUNS_SX}>
          <RunHistory workflowId={workflow.scheduledWorkflowId} selectedRunId={selectedRun?.runId ?? null} onSelect={setSelectedRun} />
        </Box>
      </Box>
    </Box>
  )
}
